import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { Repeat } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { Paginated, StandingOrderListItemDto } from '@pams/shared';
import {
  ARRANGEMENT_ANY_DATE_WINDOW,
  STANDING_ORDER_SORT_FIELDS,
  STANDING_ORDER_STATUSES,
  toTwelveHour,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import { useServerTable } from '@/hooks/useServerTable';
import { READ_ONLY_TOOLTIP, can, mayAuthorArrangement, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { COVERAGE_TONE, STANDING_ORDER_STATUS_LABEL, STANDING_ORDER_STATUS_TONE } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';
import { coverageOf, coverageState, formatDuration } from '@/lib/shiftCoverage';
import { ARRANGEMENTS_COPY } from '../Arrangements/copy';
import { COVERAGE_LABEL, RTC_COPY, RTC_LIST_COPY } from '../StandingOrderBuilder/copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ROUND-THE-CLOCK STANDING ORDERS.
 *
 *  ⚠️ A STANDING ORDER IS A GENERATOR, NOT AN ARRANGEMENT. What it emits are ordinary
 *  arrangements, which live on `/arrangements` under the second tab. This screen lists the
 *  GENERATORS, which is why "Generated" is a count here and a row there.
 *
 *  ⚠️ THE COVERAGE COLUMN IS DERIVED ON THE CLIENT, from `row.shifts[]`, through the SAME
 *  two functions the builder's bar calls. A server-computed coverage string would be a
 *  second implementation of one derivation, and the day the two disagreed this list would
 *  say "Round the clock" about a pattern the builder was drawing a hole in.
 *
 *  ⚠️ THE NAME IS THE WAY IN, AS A REAL LINK (DESIGN_LANGUAGE §4), and nothing else in the
 *  row reaches the same place. `pages/Arrangements` is recorded there as the ONE exception,
 *  not as a precedent — and the reason it needed a whole-row handler (routing two ways) does
 *  not exist here.
 */
const StandingOrdersPage = () => {
  const { officer } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const canWrite = officer ? can(officer.accessRole, 'standingOrder.write') : false;
  const canEnd = officer ? can(officer.accessRole, 'standingOrder.end') : false;
  /*
   * ⚠️ [C2b §7a] THE SAME OFFER GATE THE ARRANGEMENT LIST CARRIES, BECAUSE THE SERVER NOW
   * REFUSES THE SAME WAY. `createStandingOrder` calls `assertMayAuthorArrangement`, so without
   * this a nodal admin would press an ENABLED primary button and get a 403 — the pre-existing
   * hole [UX1 §8] closed on the arrangement list, reappearing on its sibling.
   *
   * ⚠️ IT IS COURTESY, NOT AUTHORISATION — `accessRole.ts`'s standing rule. The route keeps
   * `standingOrder.write` and the API refuses regardless of what this button does.
   */
  const { nodalsMayAuthor } = useArrangementAuthoringSetting();
  const mayRaise = officer
    ? mayAuthorArrangement(officer.accessRole, actingOrgUnitTypeOf(officer), nodalsMayAuthor)
    : false;

  const table = useServerTable<StandingOrderListItemDto, (typeof STANDING_ORDER_SORT_FIELDS)[number]>({
    sortFields: STANDING_ORDER_SORT_FIELDS,
    defaultSortBy: 'startDate',
    defaultSortOrder: 'desc',
    filterKeys: ['show', 'status', 'policyId', 'divisionOrgUnitId'],
    queryKey: (params) => queryKeys.standingOrders.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<StandingOrderListItemDto>>('/standing-orders', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...(params.search ? { search: params.search } : {}),
          ...params.filters,
        },
        signal,
      }),
  });

  const end = useMutation({
    mutationFn: (id: string) => api.post(`/standing-orders/${id}/end`, {}),
    onSuccess: () => {
      void message.success('No more orders will be created from this one');
      void queryClient.invalidateQueries({ queryKey: queryKeys.standingOrders.all });
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmEnd = (row: StandingOrderListItemDto): void => {
    modal.confirm({
      title: RTC_COPY.endConfirmTitle,
      /* ⚠️ It states what ending DOES and what it does NOT affect, before the click. */
      content: (
        <div className="flex flex-col gap-2 text-sm">
          <span>{RTC_COPY.endingWhatItDoes}</span>
          <span>{RTC_COPY.endingWhatItDoesNot}</span>
          <span className="text-muted-foreground">{RTC_COPY.endingIsFinal}</span>
        </div>
      ),
      okText: 'End it',
      okButtonProps: { danger: true },
      onOk: () => {
        end.mutate(row.id);
      },
    });
  };

  const rowActions = (row: StandingOrderListItemDto): MenuProps['items'] => [
    {
      key: 'end',
      label: 'End this order',
      danger: true,
      disabled: !canEnd || row.status === 'ENDED',
      onClick: () => {
        confirmEnd(row);
      },
    },
  ];

  const allColumns: TableProps<StandingOrderListItemDto>['columns'] = [
    {
      key: 'title',
      title: 'Order',
      sorter: true,
      render: (_value, row) => (
        <NameCell name={row.displayTitle} to={`/standing-orders/${row.id}`} secondary={row.reference} />
      ),
    },
    {
      key: 'places',
      title: 'Places',
      width: 180,
      render: (_value, row) =>
        row.primaryLocationName === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="truncate">
            {row.primaryLocationName}
            {row.placeCount > 1 && <span className="text-muted-foreground"> +{row.placeCount - 1}</span>}
          </span>
        ),
    },
    {
      key: 'divisions',
      title: 'Divisions',
      width: 150,
      render: (_value, row) =>
        row.divisions.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          row.divisions.map((division) => division.shortCode).join(', ')
        ),
    },
    {
      key: 'shifts',
      title: 'Shifts',
      width: 80,
      align: 'right',
      render: (_value, row) => (
        <span
          className="tabular-nums"
          title={row.shifts
            .map((shift) => `${toTwelveHour(shift.startTime)}–${toTwelveHour(shift.endTime)}`)
            .join(' · ')}
        >
          {row.shifts.length}
        </span>
      ),
    },
    {
      key: 'coverage',
      title: 'Coverage',
      width: 170,
      render: (_value, row) => {
        const coverage = coverageOf(row.shifts);
        const state = coverageState(coverage);
        const detail =
          state === 'GAPS'
            ? ` — ${formatDuration(coverage.gapMinutes)}`
            : state === 'OVERLAPS'
              ? ` — ${formatDuration(coverage.overlapMinutes)}`
              : '';
        return <StatusDot tone={COVERAGE_TONE[state]} label={`${COVERAGE_LABEL[state]}${detail}`} />;
      },
    },
    {
      key: 'runs',
      title: 'Runs',
      width: 190,
      render: (_value, row) => (
        <span className="text-sm">
          {row.startDate} <span className="text-muted-foreground">→</span>{' '}
          {row.endDate ?? <span className="text-muted-foreground">no end date</span>}
        </span>
      ),
    },
    {
      key: 'generated',
      title: 'Generated',
      width: 110,
      align: 'right',
      /*
       * ⚠️ [P4] A LINK AT LAST — the P3b.1 carry-forward. FR-5.5.5: a number no one can click
       * is a number no one trusts.
       *
       * ⚠️ IT CARRIES `kind=ROUND_THE_CLOCK` AS WELL AS THE ID, AND THAT IS NOT OPTIONAL.
       * `listArrangementsQuerySchema.kind` DEFAULTS to `STANDARD`, and everything a generator
       * emits is round-the-clock — so the id on its own lands on an empty table, which reads
       * as "the generator produced nothing".
       *
       * ⚠️ AND ZERO IS PLAIN TEXT. A link to a list that is guaranteed empty is a promise the
       * next click breaks.
       */
      render: (_value, row) =>
        row.generatedCount > 0 ? (
          <Tooltip title={RTC_LIST_COPY.generatedHint}>
            <Link
              className="tabular-nums text-link underline-offset-4 hover:underline"
              /*
               * ⚠️ [C3c.1] AND IT CARRIES AN EXPLICIT WINDOW, FOR THE SAME KIND OF REASON. §8a
               * gave the list a FORWARD seven-day default, and this link means "everything this
               * generator produced" — a set that begins in the past. Without the dates the count
               * beside it said N and the list it opens showed fewer, which is the same broken
               * promise the zero-is-plain-text rule above exists to prevent.
               */
              to={
                `/arrangements?kind=ROUND_THE_CLOCK&standingOrderId=${row.id}` +
                `&dateFrom=${ARRANGEMENT_ANY_DATE_WINDOW.dateFrom}&dateTo=${ARRANGEMENT_ANY_DATE_WINDOW.dateTo}`
              }
            >
              {row.generatedCount}
            </Link>
          </Tooltip>
        ) : (
          <Tooltip title={RTC_LIST_COPY.generatedHint}>
            <span className="tabular-nums text-subtle">{row.generatedCount}</span>
          </Tooltip>
        ),
    },
    {
      key: 'status',
      title: 'Status',
      width: 110,
      render: (_value, row) => (
        <StatusDot tone={STANDING_ORDER_STATUS_TONE[row.status]} label={STANDING_ORDER_STATUS_LABEL[row.status]} />
      ),
    },
    {
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      title: '',
      width: 60,
      align: 'right',
      render: (_value, row) => (
        <Dropdown menu={{ items: rowActions(row) }} trigger={['click']}>
          <Button type="text" size="small" aria-label={`More actions for ${row.displayTitle}`}>
            ⋯
          </Button>
        </Dropdown>
      ),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'standing-orders' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  /*
   * ⚠️ `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never reaches the
   * URL, which keeps a shared link clean; the page re-defaults on the way out, exactly as
   * `kind` does.
   */
  const show = table.params.filters.show || 'current';

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={Repeat}
        title={RTC_LIST_COPY.title}
        /* ⚠️ [PRE-P6.2] NO SUBTITLE. The three operations screens carry a title and nothing
           else — a standing description under a heading is read once and then occupies a
           line of every visit for ever. */
        actions={
          <Tooltip title={mayRaise ? (canWrite ? undefined : READ_ONLY_TOOLTIP) : ARRANGEMENTS_COPY.mayNotRaise}>
            <Button
              type="primary"
              disabled={!canWrite || !mayRaise}
              onClick={() => {
                void navigate('/standing-orders/new');
              }}
            >
              {RTC_LIST_COPY.create}
            </Button>
          </Tooltip>
        }
        filters={
          <>
            {/*
              ⚠️ [LIFECYCLE] TWO AXES, TWO CONTROLS — never one mixed dropdown. `Show` says
              whether a row is in circulation; `Status` says where it is in its own life.
              Collapsing them would make "Archived" look like a peer of "Draft", which is the
              confusion the archive-is-an-axis design exists to prevent.

              ⚠️ A `Select`, NOT a `Segmented` — three values stop it being a switch, and here
              it would sit beside a Segmented and read as a second tab bar. No `allowClear`:
              "Current" is a value, not the absence of a filter.
            */}
            <FilterField label="Show" className="w-44">
              <Select
                className="w-full"
                value={show}
                onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                options={[
                  { value: 'current', label: 'Current' },
                  { value: 'archived', label: 'Archived' },
                  { value: 'all', label: 'All' },
                ]}
                data-testid="show-filter"
              />
            </FilterField>
            <FilterField label="Search" className="w-56">
              <Input.Search
                allowClear
                placeholder="Title or order number"
                value={table.searchInput}
                onChange={(event) => {
                  table.onSearchChange(event.target.value);
                }}
              />
            </FilterField>
            <FilterField label="Status" className="w-40">
              <Select
                allowClear
                placeholder="Any"
                className="w-full"
                value={table.params.filters.status || undefined}
                onChange={(value: string | undefined) => {
                  table.setFilter('status', value);
                }}
                options={STANDING_ORDER_STATUSES.map((status) => ({
                  value: status,
                  label: STANDING_ORDER_STATUS_LABEL[status],
                }))}
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<StandingOrderListItemDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState
              size="sm"
              title={table.params.search ? RTC_LIST_COPY.emptySearch : RTC_LIST_COPY.emptyTitle}
              description={table.params.search ? undefined : RTC_LIST_COPY.emptyDescription}
            />
          ),
        }}
      />
    </div>
  );
};

export default StandingOrdersPage;
